import {fabric} from "fabric";
import { DirectionEnum, ObjectSuperTypesEnum, ObjectTypesEnum } from "../../../enums";
import { SeatFactory } from "../factories/seat.factory";
import { RowSeatLabelHelper } from "./RowSeatLabelHelper";

export class RectTableSeatHelper {
  static async rebuildSeats({
    rectTable,
    table,
    tableGroup,
    newQuantity,
    direction,
    seatRadius,
    seatGap,
    setSeatValue,
  }: any) {
    rectTable.set({
      seatsQuantity: {
        ...rectTable.seatsQuantity,
        [direction]: newQuantity,
      },
    });

    this.removeSeats(rectTable);

    const positions = this.getSeatPositions({
      seatsQuantity: rectTable.seatsQuantity,
      table,
      tableGroup,
      seatRadius,
      seatGap,
    });

    const newSeats = await this.createSeats({
      positions,
      rowSeatSize: seatRadius * 2,
      category: rectTable.category,
      tableLabel: rectTable.tableLabeling.label,
      sectionLabel: rectTable.section.label
    });

    const prevCenter = rectTable.getCenterPoint();

    newSeats.forEach((seat: any) => {
      rectTable.add(seat);
    });

    rectTable.addWithUpdate();

    rectTable.setPositionByOrigin(prevCenter, 'center', 'center');
    rectTable.setCoords();

    const updatedSeats = this.getSeats(rectTable);

    this.applyDistribution({
      rectTable,
      seats: updatedSeats,
      table,
      setSeatValue,
    });

    updatedSeats.forEach((seat: any) => {
      setSeatValue(seat, rectTable, seat.label);
    });

    return updatedSeats;
  }

  static getRectTableParts(rectTable: any) {
    const tableGroup = rectTable
      .getObjects(ObjectTypesEnum.Group)
      .find((obj: any) => obj.superType === ObjectSuperTypesEnum.Table);

    const table = tableGroup
      ?.getObjects()
      .find((obj: any) => obj.type === ObjectTypesEnum.Rect);

    return {
      tableGroup,
      table,
    };
  }

  static removeSeats(rectTable: any) {
    const seats = rectTable
      .getObjects()
      .filter(
        (obj: any) =>
          obj.superType === ObjectSuperTypesEnum.Seat
      );

    seats.forEach((seat: any) => {
      rectTable.remove(seat);
    });
  }

  static getSeats(rectTable: any) {
    return rectTable
      .getObjects()
      .filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Seat);
  }

  static applyDistribution({
    rectTable,
    seats,
    table,
    setSeatValue,
  }: any) {
    const orderedSeats = this.getOrderedTableSeats({
      seats,
      table,
    });

    RowSeatLabelHelper.applySeatDistribution({
      selectedItem: rectTable,
      seats: orderedSeats,
      distributionKey: rectTable.seatLabeling.key,
      startsAt: rectTable.seatLabeling.startsAt,
      setSeatValue,
      distributionObject: 'seatLabeling'
    });
  }

  static getOrderedTableSeats({ seats, table }: any) {
    if (!seats?.length) return [];

    const tableCenter = this.getObjectCanvasCenter(table);
    const tableWidth = table.getScaledWidth();
    const tableHeight = table.getScaledHeight();

    const tableBounds = {
      left: tableCenter.x - tableWidth / 2,
      right: tableCenter.x + tableWidth / 2,
      top: tableCenter.y - tableHeight / 2,
      bottom: tableCenter.y + tableHeight / 2,
    };

    const seatsWithPosition = seats.map((seat: any) => {
      const center = this.getObjectCanvasCenter(seat);

      return {
        seat,
        x: center.x,
        y: center.y,
        side: this.getSeatTableSide(center, tableBounds),
      };
    });

    const leftSeats = seatsWithPosition
      .filter((item: any) => item.side === DirectionEnum.Left)
      .sort((a: any, b: any) => b.y - a.y);

    const topSeats = seatsWithPosition
      .filter((item: any) => item.side === DirectionEnum.Up)
      .sort((a: any, b: any) => a.x - b.x);

    const rightSeats = seatsWithPosition
      .filter((item: any) => item.side === DirectionEnum.Right)
      .sort((a: any, b: any) => a.y - b.y);

    const bottomSeats = seatsWithPosition
      .filter((item: any) => item.side === DirectionEnum.Down)
      .sort((a: any, b: any) => b.x - a.x);

    return [
      ...leftSeats,
      ...topSeats,
      ...rightSeats,
      ...bottomSeats,
    ].map((item: any) => item.seat);
  }

  static getSeatTableSide(
    seatCenter: { x: number; y: number },
    tableBounds: { left: number; right: number; top: number; bottom: number }
  ) {
    if (seatCenter.y < tableBounds.top) return DirectionEnum.Up;
    if (seatCenter.y > tableBounds.bottom) return DirectionEnum.Down;
    if (seatCenter.x < tableBounds.left) return DirectionEnum.Left;

    return DirectionEnum.Right;
  }

  static getObjectCanvasCenter(obj: any) {
    const center = obj.getCenterPoint();

    return {
      x: center.x,
      y: center.y,
    };
  }

  static getSeatPositions({
    seatsQuantity,
    table,
    tableGroup,
    seatRadius,
    seatGap,
  }: any) {
    const width = table.getScaledWidth();
    const height = table.getScaledHeight();

    const offset = seatRadius + seatGap;
    const diameter = seatRadius * 2;

    const centerX = tableGroup.left ?? 0;
    const centerY = tableGroup.top ?? 0;

    return [
      ...this.getHorizontalPositions({
        count: seatsQuantity.up,
        width,
        y: centerY - height / 2 - offset,
        centerX,
        side: DirectionEnum.Up,
        diameter,
        seatRadius,
      }),
      ...this.getHorizontalPositions({
        count: seatsQuantity.down,
        width,
        y: centerY + height / 2 + offset,
        centerX,
        side: DirectionEnum.Down,
        diameter,
        seatRadius,
      }),
      ...this.getVerticalPositions({
        count: seatsQuantity.left,
        height,
        x: centerX - width / 2 - offset,
        centerY,
        side: DirectionEnum.Left,
        diameter,
        seatRadius,
      }),
      ...this.getVerticalPositions({
        count: seatsQuantity.right,
        height,
        x: centerX + width / 2 + offset,
        centerY,
        side: DirectionEnum.Right,
        diameter,
        seatRadius,
      }),
    ];
  }

  private static getHorizontalPositions({
    count,
    width,
    y,
    centerX,
    side,
    diameter,
    seatRadius,
  }: any) {
    if (count <= 0) return [];

    const totalSeats = count * diameter;
    const gap = (width - totalSeats) / (count + 1);

    let currentX = centerX - width / 2 + gap + seatRadius;

    return Array.from({ length: count }, () => {
      const position = {
        left: currentX,
        top: y,
        side,
      };

      currentX += diameter + gap;

      return position;
    });
  }

  private static getVerticalPositions({
    count,
    height,
    x,
    centerY,
    side,
    diameter,
    seatRadius,
  }: any) {
    if (count <= 0) return [];

    const totalSeats = count * diameter;
    const gap = (height - totalSeats) / (count + 1);

    let currentY = centerY - height / 2 + gap + seatRadius;

    return Array.from({ length: count }, () => {
      const position = {
        left: x,
        top: currentY,
        side,
      };

      currentY += diameter + gap;

      return position;
    });
  }

  static async createSeats({
    positions,
    rowSeatSize,
    category,
    tableLabel,
    sectionLabel
  }: any): Promise<fabric.Group[]> {
    const seats = await Promise.all(
      positions.map(async ({ left, top, side }: any) => {
        const seat = await SeatFactory.create({
          left,
          top,
          rowSeatSize,
          defaultSeatColor: category.color,
          tableLabel,
          sectionLabel
        });

        seat.set({
          side,
          category: { ...category },
        });

        const shape = seat.item(0);

        shape.set({
          fill: `${category.color}40`,
          stroke: category.color,
        });

        return seat;
      })
    );

    return seats;
  }

  static calculateMaxSeats({
    table,
    seatRadius,
  }: any) {
    const diameter = seatRadius * 2;
    return {
      horizontal: Math.floor(table.width! / diameter),
      vertical: Math.floor(table.height! / diameter),
    };
  }

  static repositionExistingSeats({
    rectTable,
    table,
    tableGroup,
    seatRadius,
    seatGap,
  }: any) {
    const seats = this.getSeats(rectTable);
    const grouped = {
      [DirectionEnum.Up]: seats.filter((seat: any) => seat.side === DirectionEnum.Up),
      [DirectionEnum.Down]: seats.filter((seat: any) => seat.side === DirectionEnum.Down),
      [DirectionEnum.Left]: seats.filter((seat: any) => seat.side === DirectionEnum.Left),
      [DirectionEnum.Right]: seats.filter((seat: any) => seat.side === DirectionEnum.Right),
    };
    const positions = this.getSeatPositions({
      seatsQuantity: {
        up: grouped[DirectionEnum.Up].length,
        down: grouped[DirectionEnum.Down].length,
        left: grouped[DirectionEnum.Left].length,
        right: grouped[DirectionEnum.Right].length,
      },
      table,
      tableGroup,
      seatRadius,
      seatGap,
    });
    const positionsBySide = {
      [DirectionEnum.Up]: positions.filter((pos: any) => pos.side === DirectionEnum.Up),
      [DirectionEnum.Down]: positions.filter((pos: any) => pos.side === DirectionEnum.Down),
      [DirectionEnum.Left]: positions.filter((pos: any) => pos.side === DirectionEnum.Left),
      [DirectionEnum.Right]: positions.filter((pos: any) => pos.side === DirectionEnum.Right),
    };

    for (const side of [
      DirectionEnum.Up,
      DirectionEnum.Down,
      DirectionEnum.Left,
      DirectionEnum.Right,
    ]) {
      grouped[side].forEach((seat: any, index: number) => {
        const position = positionsBySide[side][index];
        if (!position) return;

        seat.setPositionByOrigin(
          new fabric.Point(position.left, position.top),
          'center',
          'center'
        );


        seat.setCoords();
      });
    }
  }
}